/**
 * ModerationPanel Component
 *
 * Displays pending FAQs for moderator review and approval
 * Extracted from FAQs.tsx as part of Phase 3 decomposition
 *
 * Responsibilities:
 * - Display list of pending FAQs
 * - Show FAQ metadata (author, date, visibility, categories)
 * - Provide approval/rejection actions for moderators
 * - Handle loading and error states
 */

import React from 'react';
import type { ReactElement } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
  Skeleton,
  List,
  ListItem,
  Avatar,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { FAQDocumentResponse, DocumentVisibility, ApprovalStatus } from '../../api/data-contracts';
import { ApprovalStatusBadge } from './ApprovalStatusBadge';
import { ApprovalActions } from './ApprovalActions';
import { getUserDisplayName, formatDate } from '../../utils/formatting/formatters';

export interface ModerationPanelProps {
  /** Array of pending FAQ documents */
  pendingFAQs: FAQDocumentResponse[];
  /** Whether data is currently loading */
  isLoading: boolean;
  /** Error object if loading failed */
  error: Error | null;
  /** Function to check approval/rejection permissions */
  canApproveRejectFAQ: (faq: FAQDocumentResponse) => {
    canApprove: boolean;
    canReject: boolean;
    canWithdraw: boolean;
  };
  /** Function to get visibility icon */
  getVisibilityIcon: (visibility: DocumentVisibility) => ReactElement;
  /** Function to get visibility text */
  getVisibilityText: (faq: FAQDocumentResponse) => string;
  /** Callback to show success message */
  showSuccess: (message: string) => void;
  /** Callback to show error message */
  showError: (message: string) => void;
}

/**
 * Memoized Pending FAQ Item Component
 */
const PendingFAQItem = React.memo<{
  faq: FAQDocumentResponse;
  canApprove: boolean;
  canReject: boolean;
  canWithdraw: boolean;
  getVisibilityIcon: (visibility: DocumentVisibility) => ReactElement;
  getVisibilityText: (faq: FAQDocumentResponse) => string;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
}>(({
  faq,
  canApprove,
  canReject,
  canWithdraw,
  getVisibilityIcon,
  getVisibilityText,
  showSuccess,
  showError
}) => {
  const { t, i18n } = useTranslation();

  return (
    <ListItem
      sx={{
        display: 'block',
        py: 2,
      }}
    >
      {/* Question & Answer */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {faq.question || faq.title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {faq.answer}
        </Typography>
      </Box>

      {/* Metadata Row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* Status Badge */}
          <ApprovalStatusBadge status={faq.approval_status || ApprovalStatus.Draft} />

          {/* Author Info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Avatar sx={{ width: 20, height: 20, fontSize: '0.75rem' }}>
              <PersonIcon fontSize="small" />
            </Avatar>
            <Typography variant="caption" color="text.secondary">
              {getUserDisplayName(faq, t('common.unknown_user', 'Unknown User'))}
            </Typography>
          </Box>

          {/* Date */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              {faq.created_at ? formatDate(faq.created_at, i18n.language) : '-'}
            </Typography>
          </Box>

          {/* Visibility */}
          <Tooltip title={getVisibilityText(faq)}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {getVisibilityIcon(faq.visibility || DocumentVisibility.Organization)}
            </Box>
          </Tooltip>

          {/* Categories */}
          {faq.category_tags?.slice(0, 2).map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          ))}
          {faq.category_tags && faq.category_tags.length > 2 && (
            <Typography variant="caption" color="text.secondary">
              +{faq.category_tags.length - 2} more
            </Typography>
          )}
        </Box>

        {/* Approval Actions */}
        <ApprovalActions
          faq={faq}
          canApprove={canApprove}
          canReject={canReject}
          canWithdraw={canWithdraw}
          variant="buttons"
          size="small"
          onSuccess={showSuccess}
          onError={showError}
        />
      </Box>
    </ListItem>
  );
});

PendingFAQItem.displayName = 'PendingFAQItem';

/**
 * ModerationPanel - Display pending FAQs for review
 *
 * Performance: Wrapped in React.memo to prevent unnecessary re-renders
 * Each pending FAQ item is also memoized individually
 *
 * @example
 * ```tsx
 * <ModerationPanel
 *   pendingFAQs={pendingFAQs.faqs}
 *   isLoading={pendingLoading}
 *   error={pendingError}
 *   canApproveRejectFAQ={canApproveRejectFAQ}
 *   getVisibilityIcon={getVisibilityIcon}
 *   getVisibilityText={getVisibilityText}
 *   showSuccess={showSuccess}
 *   showError={showError}
 * />
 * ```
 */
export const ModerationPanel = React.memo<ModerationPanelProps>(({
  pendingFAQs,
  isLoading,
  error,
  canApproveRejectFAQ,
  getVisibilityIcon,
  getVisibilityText,
  showSuccess,
  showError
}) => {
  const { t } = useTranslation();

  // Loading state
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error">
        {t('faq.moderation.error.loading_pending')}
      </Alert>
    );
  }

  // Empty state
  if (!pendingFAQs || pendingFAQs.length === 0) {
    return (
      <Alert severity="info">
        {t('faq.moderation.no_pending_faqs')}
      </Alert>
    );
  }

  // Pending FAQs list
  return (
    <Paper variant="outlined">
      <List>
        {pendingFAQs.map((faq, index) => {
          const permissions = canApproveRejectFAQ(faq);
          return (
            <React.Fragment key={faq.document_id}>
              {index > 0 && <Divider />}
              <PendingFAQItem
                faq={faq}
                canApprove={permissions.canApprove}
                canReject={permissions.canReject}
                canWithdraw={permissions.canWithdraw}
                getVisibilityIcon={getVisibilityIcon}
                getVisibilityText={getVisibilityText}
                showSuccess={showSuccess}
                showError={showError}
              />
            </React.Fragment>
          );
        })}
      </List>
    </Paper>
  );
});

ModerationPanel.displayName = 'ModerationPanel';
